import React, { useState } from "react";
import { Building2, CalendarPlus, CheckCircle2, CircleDashed, Clock, IndianRupee, RefreshCw, ChevronRight } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { fmtINR } from "../lib/api";

const fmtN = (n) => Number(n || 0).toLocaleString("en-IN");

function SummaryCard({ icon: Icon, label, value, sub, tone = "", onClick, testId, hint }) {
  const clickable = typeof onClick === "function";
  const body = (
    <CardContent className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground truncate">{label}</p>
          <p className={`text-xl font-semibold tabular-nums mt-1 font-heading truncate ${tone}`} data-testid={`${testId}-value`}>{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground mt-1 truncate" data-testid={`${testId}-sub`}>{sub}</p>}
        </div>
        <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-accent-foreground" />
        </div>
      </div>
      {clickable && (
        <p className="text-[11px] text-primary mt-2 flex items-center gap-0.5">{hint || "View breakup"} <ChevronRight className="h-3 w-3" /></p>
      )}
    </CardContent>
  );
  if (clickable) {
    return (
      <Card className="shadow-sm hover:shadow-md hover:border-primary/40 focus-within:ring-2 focus-within:ring-ring transition-shadow" data-testid={testId}>
        <button type="button" onClick={onClick} className="w-full text-left rounded-xl focus-visible:outline-none" aria-label={`${label} — ${hint || "view breakup"}`}
          data-testid={`${testId}-button`}>
          {body}
        </button>
      </Card>
    );
  }
  return <Card className="shadow-sm" data-testid={testId}>{body}</Card>;
}

/** Month-wise breakup of properties added in the current FY (Apr → Mar). */
export function NewPropertiesBreakupDialog({ open, onOpenChange, newFy }) {
  const months = newFy?.months || [];
  const max = Math.max(1, ...months.map((m) => m.count || 0));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="new-properties-breakup-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarPlus className="h-4 w-4 text-primary" /> New properties — FY {newFy?.label}
          </DialogTitle>
          <DialogDescription>
            Month-wise count of properties added between {newFy?.start} and {newFy?.end} (IST), within the current filters.
            Total <span className="font-semibold text-foreground tabular-nums" data-testid="new-properties-breakup-total">{fmtN(newFy?.count)}</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
          <Table data-testid="new-properties-breakup-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Month</TableHead>
                <TableHead>Share</TableHead>
                <TableHead className="text-right w-24">Added</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {months.map((m) => (
                <TableRow key={m.month} data-testid={`new-properties-month-${m.month}`} className={m.count ? "" : "text-muted-foreground"}>
                  <TableCell className="font-medium">{m.label}</TableCell>
                  <TableCell>
                    <div className="h-2 rounded-full bg-primary/15 overflow-hidden" aria-hidden="true">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${Math.round((m.count || 0) * 100 / max)}%`, transition: "width 200ms" }} />
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-semibold" data-testid={`new-properties-month-${m.month}-count`}>{fmtN(m.count)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Phase 140 — six-card summary header of the Properties register. `data` is GET /properties/summary for the
 * SAME filters as the list; `scopeLabel` explains supervisor zone scoping.
 */
export function PropertyRegisterSummary({ data, loading, error, onRetry, filtered = false }) {
  const [open, setOpen] = useState(false);

  if (loading && !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4" data-testid="properties-summary-loading">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="shadow-sm"><CardContent className="p-4 space-y-2">
            <Skeleton className="h-3 w-24" /><Skeleton className="h-6 w-20" /><Skeleton className="h-3 w-28" />
          </CardContent></Card>
        ))}
      </div>
    );
  }
  if (error && !data) {
    return (
      <Card className="shadow-sm mb-4 border-destructive/30" data-testid="properties-summary-error">
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <p className="text-sm text-destructive">{String(error)}</p>
          <Button size="sm" variant="outline" onClick={onRetry} data-testid="properties-summary-retry"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry</Button>
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const pct = (n) => (data.total ? `${Math.round((n || 0) * 100 / data.total)}% of ${filtered ? "filtered" : "register"}` : "");
  return (
    <div className="mb-4" data-testid="properties-summary">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-muted-foreground">
          {filtered ? "Summary of the filtered properties" : "Summary of the whole register"}
          {data.scope === "supervisor_zones" ? " — your mapped zones only" : ""}
        </p>
        {loading && <Badge variant="outline" className="text-[10px] gap-1"><RefreshCw className="h-3 w-3 animate-spin" /> Updating</Badge>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <SummaryCard icon={Building2} label="Total properties" value={fmtN(data.total)}
          sub={`Demand ${fmtINR(data.total_demand)}`} testId="summary-total-properties" />
        <SummaryCard icon={CalendarPlus} label={`New in FY ${data.new_fy?.label}`} value={fmtN(data.new_fy?.count)}
          sub="Added this financial year" onClick={() => setOpen(true)} hint="Month-wise breakup" testId="summary-new-properties" />
        <SummaryCard icon={CheckCircle2} label="Collected properties" value={fmtN(data.collected?.count)}
          sub={pct(data.collected?.count) || "Fully paid"} tone="text-emerald-700" testId="summary-collected-properties" />
        <SummaryCard icon={CircleDashed} label="Partial collected" value={fmtN(data.partial?.count)}
          sub={`${fmtINR(data.partial?.balance)} still due`} tone="text-amber-700" testId="summary-partial-properties" />
        <SummaryCard icon={Clock} label="Pending properties" value={fmtN(data.pending?.count)}
          sub={`${fmtINR(data.pending?.amount)} pending`} tone="text-destructive" testId="summary-pending-properties" />
        <SummaryCard icon={IndianRupee} label="Total collected amount" value={fmtINR(data.collected_amount)}
          sub={`${fmtINR(data.total_balance)} balance overall`} tone="text-primary" testId="summary-collected-amount" />
      </div>
      <NewPropertiesBreakupDialog open={open} onOpenChange={setOpen} newFy={data.new_fy} />
    </div>
  );
}
